import React, { useEffect, useState, useCallback, useMemo } from 'react'
import Menu from '../../components/Menu/Menu'
import LoaderDialog from '../../components/Loader/Loader'
import iconBinance from '../../assets/images/bnb-icon.svg'
import clouds from '../../assets/images/clouds-main.png'
import NFTShowcase from '../../components/NFTShowcase/NFTShowcase'
import TextDialog from '../../components/TextDialog/TextDialog'

import { getToken, CactiContract } from '../../utils/contracts'
import {
  BN,
  divide,
  equalTo,
  greaterThan,
  multiply,
} from '../../helpers/bignumber'
import { useWallet } from '@binance-chain/bsc-use-wallet'
import { sendTransaction, web3 } from '../../utils/web3'
import { getAccountTransactionsBasedOnContract } from '../../helpers/calls'
import { config } from '../../utils/config'
import { truncateFromCenter } from '../../helpers/sharedFunctions'

function Home() {
  const wallet = useWallet()
  const { ethereum }: { ethereum: any } = useWallet()
  const { account } = useWallet()

  const [value, setValue] = useState(1)

  const [startingIndex, setStartingIndex] = useState(0)
  const [showcaseNFTSIsLoading, setShowcaseNFTSIsLoading] = useState(false)
  const [textDialogState, setTextDialogState] = useState(false)

  const [totalSupply, setTotalSupply] = useState(0)
  const [maxTotalSupply, setMaxTotalSupply] = useState(0)

  const [pricePerToken, setPricePerToken] = useState(BN(0).toString())
  const [maxTokensPerTransaction, setMaxTokensPerTransaction] = useState(BN(20).toString())

  const [boughtNFTS, setBoughtNFTS]: any = useState([])
  const [showcaseNFTS, setShowcaseNFTS]: any = useState([])

  const [loaderDialogState, setLoaderDialogState] = useState(false)

  const [loadingText, setLoadingText] = useState(['', ''])
  const [btnText, setBtnText] = useState('Connect')

  const [hasMinted, setHasMinted] = useState(false)
  const [isViewingCollection, setIsViewingCollection] = useState(false)
  const [isTransactionBusy, setIsTransactionBusy] = useState(false)
  const [publicSaleIsActive, setPublicSaleIsActive] = useState(false)

  /**
   * @dev ETH VALUE based on your input
   */
  const buyAmount = useMemo(() => multiply(multiply(value, pricePerToken), 1e18), [pricePerToken, value])

  /**
   * @dev Shows the amount based on the buy amount and value
   */
  const showAmount = useMemo(() => divide(buyAmount, 1e18).toString() !== 'NaN' ? divide(buyAmount, 1e18).toString() : '0', [buyAmount])

  /**
   * @dev Get the current price for 1 NFT
   */
  const getPricePerNFT = async () => {
    const priceInWei = await CactiContract.methods.PRICE().call()
    const priceInEth = divide(priceInWei, 1e18)
    setPricePerToken(BN(priceInEth).toString())
  }

  /**
   * @dev Get the MAX tokens per transaction you can MINT
   */
  const getMaxTokensPerTX = async () => {
    const max = await CactiContract.methods.MAX_MINT_PER_TX().call()
    setMaxTokensPerTransaction(max)
  }

  /**
   * @dev Get the MAX tokens per transaction you can MINT
   */
  const getPublicSaleState = async () => {
    const saleIsActive = await CactiContract.methods.publicSaleIsActive().call()
    setPublicSaleIsActive(saleIsActive)
  }

  /**
   * @dev Get the MAX tokens per transaction you can MINT
   */
   const getStartingIndex = async () => {
    const idx = await CactiContract.methods.startingIndex().call()
    setStartingIndex(idx)
  }

  /**
  * @dev Get the current minted supply
  */
  const getTotalSupply = async () => {
    const totalSupply = await CactiContract.methods.totalSupply().call()
    setTotalSupply(BN(totalSupply).toNumber())
  }

  /**
  * @dev Get the max supply that can be minted
  */
  const getMaxTotalSupply = async () => {
    const MAX_TOTAL_SUPPLY = await CactiContract.methods.TOTAL_SUPPLY().call()
    setMaxTotalSupply(BN(MAX_TOTAL_SUPPLY).toNumber())
  }

  /**
  * @dev Check if the current value is a float
  * @param N Could be a string | number | float
  */
  const isFloat = (n: any) => {
    return (
      !isNaN(n) &&
      (function (x) {
        return (x | 0) === x
      })(parseFloat(n))
    )
  }

  const hasFailedCallback = () => {
    setLoadingText(['Transaction failed', 'Try again...'])
    setTimeout(() => {
      setLoaderDialogState(false)
      setIsTransactionBusy(false)
    }, 2000)
  }

  /**
  * @dev Get your bought NFT's based on etherscan api transactions
  */
  const getBoughtNFTS = useCallback(async () => {
    if (account !== null) {
      setShowcaseNFTSIsLoading(true)
      const transactions = await getAccountTransactionsBasedOnContract(account)
      const result = transactions.result
      let arr: Array<any> = []
      for (let i = 0; i < result.length; i++) {
        // Get Bought Token ID's
        const tokenID = result[i].tokenID
        const baseURL = await CactiContract.methods.tokenURI(tokenID).call()
        const res = await fetch(baseURL, { method: 'GET' })
        const json = await res.json()
        if (json) {
          json.tokenID = tokenID
          arr.push(json)
        }
      }
      if (arr) {
        setBoughtNFTS(arr)
        setShowcaseNFTSIsLoading(false)
      }
    }
  }, [account])

  /**
   * @dev Makes a transaction to mint a NFT for the public sale
   *
   * After the transaction the total supply will get updated
   */
  const mintPublic = async () => {
    setLoaderDialogState(true)
    setLoadingText([
      'Creating transaction',
      'Track your wallet for further details and progression.',
    ])
    setIsTransactionBusy(true)
    const amountOfTokens = BN(value).toNumber()
    const func = await CactiContract.methods.mintCacti(amountOfTokens).encodeABI()
    try {
      await sendTransaction(ethereum, account, getToken('Cacti').address, func, buyAmount, async (tx: any) => {
        setLoadingText([
          "Retrieving Cacti",
          'We are almost there. Any moment now...',
        ])
        let arr: any = []
        if (account) {
          for (let i = 0; i < tx.logs.length; i++) {
            const indexInHex = tx.logs[i].topics[3];
            const number = web3.utils.hexToNumber(indexInHex);
            const baseURL = await CactiContract.methods.tokenURI(number).call();
            const res = await fetch(baseURL, { method: 'GET' });
            const json = await res.json();
            json.tokenID = number;
            arr.push(json);
            if (arr.length === BN(value).toNumber()) {
              setLoaderDialogState(false)
              setHasMinted(true)
              setIsViewingCollection(true)
              setShowcaseNFTS(arr)
              setBoughtNFTS(boughtNFTS.concat(arr)); // combine the bought nft's with the showcase in 1 array
              setIsTransactionBusy(false)
            }
          }
        }
      },
        (data: any) => {
          hasFailedCallback();
        },
      )
    } catch {
      hasFailedCallback();
    }
  }

  /**
 * @dev Connect wallet to metamask
 */
  const connectMetamask = useCallback(() => {
    // is web3 undefined (not installed) show messages
    if (typeof window.ethereum == 'undefined') {
      setTextDialogState(true)
    } else {
      if (wallet.status !== 'connected') {
        wallet.connect('injected');
      }
    }
    // ----
  }, [wallet])

  /**
   * @dev Based on the connected chain check if you need to show the wallet address or the wrong network txt
   */
  const getConnectButtonTxt = useCallback(async () => {
    // 0x38 = Binance Mainnet
    // 0x61 = Binance Testnet
    const id =
      config.networkId === 1 ? '0x1' :
        config.networkId === 4 ? '0x4' :
          config.networkId === 2 ? '0x2a' :
            config.networkId === 56 ? '0x38' :
              config.networkId === 97 ? '0x61'
                : '0x1';

    if (window.ethereum) {
      window.ethereum.on('chainChanged', (changedChainID: any) => {
        if (changedChainID === id) return window.location.reload()
        if (changedChainID !== id) return setBtnText('Wrong network')
        else return setBtnText('Connect')
      })
      const chainId = await window.ethereum.request({ method: 'eth_chainId' });
      if (chainId !== id) return setBtnText('Wrong network')
      if (chainId === id && wallet && wallet.account) return setBtnText(truncateFromCenter(wallet.account))
      else return setBtnText('Connect')
    }
  }, [wallet])

  useEffect(() => {
    getTotalSupply();
    getMaxTotalSupply();
    getPricePerNFT();
    getMaxTokensPerTX();
    getPublicSaleState();
    getStartingIndex();
  }, [])

  useEffect(() => {
    getBoughtNFTS()
  }, [getBoughtNFTS])

  useEffect(() => {
    getConnectButtonTxt()
  }, [getConnectButtonTxt])

  return (
    <>
      <div id="main-wrapper">
        <Menu
          onClickCollection={() => {
            setIsViewingCollection(!isViewingCollection)
            if (showcaseNFTS.length > 0) {
              setShowcaseNFTS([])
              setHasMinted(false)
            }
          }}
          showCollection={!isTransactionBusy}
        />
        <div id="bg-wrapper"></div>
        <div className="clouds-wrapper">
          <div className="clouds-1">
            <img src={clouds} alt="Cacti Club" />
          </div>
          <div className="clouds-2">
            <img src={clouds} alt="Cacti Club" />
          </div>
        </div>
        <div className="block-wrapper">
          {/* BUY NFT */}
          <div className={`block-main ${!isViewingCollection && !loaderDialogState ? 'active' : ''}`}>
            <div className="block-content">
              <div className="banner-main-top">
                <h1>Mint your Cacti</h1>
                <p>
                  An eclectic collection of 7777 randomly-generated, use
                  case-oriented 3D NFTs spiking up on the BSC blockchain.
                </p>
              </div>
              <div className="banner-main-price-block">
                <div className="banner-main-price-icon">
                  <img src={iconBinance} alt="BNB" />
                </div>
                <div className="banner-main-price">
                  <div>
                    {value > BN(maxTokensPerTransaction).toNumber()
                      ? 0
                      : showAmount
                    }
                  </div>
                  <span>BNB</span>
                </div>
              </div>
              <div className="banner-main-input">
                <div className="banner-main-input-buttons" id="banner-main-input-btm">
                  <button className="button chip-btn" onClick={() => setValue(2)}>
                    <span>2</span>
                  </button>
                  <button className="button chip-btn" onClick={() => setValue(5)}>
                    <span>5</span>
                  </button>
                  <button className="button chip-btn" onClick={() => setValue(8)}>
                    <span>8</span>
                  </button>
                  <button className="button chip-btn" onClick={() => setValue(10)}>
                    <span>10</span>
                  </button>
                  <button className="button chip-btn" onClick={() => setValue(15)}>
                    <span>15</span>
                  </button>
                  <button className="button chip-btn" onClick={() => setValue(20)}>
                    <span>20</span>
                  </button>
                </div>
                {window.innerWidth <= 768 && !account &&
                  <div className='disclaimer-box'>
                    <p><b>Note:</b> Make sure you are connected with the Binance Smart Chain network.</p>
                  </div>
                }
                <div className="banner-main-input-top" id="banner-main-input-top">
                  <input value={value} onChange={(e: any) => setValue(e.target.value)} />
                  <button
                    id="mint-btn"
                    style={{ minWidth: 100 }}
                    disabled={account ? !publicSaleIsActive || equalTo(totalSupply, maxTotalSupply) || greaterThan(value, maxTokensPerTransaction) || !isFloat(value) || equalTo(value, 0) : false}
                    className={`button btn-main mint-btn ${loaderDialogState && 'loading'}`}
                    onClick={() => account ? mintPublic() : connectMetamask()}>
                    <span>{account ? 'Sold out' : btnText}</span>
                  </button>
                </div>
              </div>
            </div>
            <div className="block-footer">
              <div className="footer-copyright">
                &copy; Copyright <span id="copyright-year">2021</span> Cacti Club
              </div>
              <ul className="footer-social">
                <li>
                  <a href="https://discord.com/invite/ZvZSbhEmdR" target="_blank" title="Discord" rel="noreferrer">
                    <i className="fab fa-discord"></i>
                  </a>
                </li>
                <li>
                  <a href="https://twitter.com/TheCactiClub" target="_blank" title="Twitter" rel="noreferrer">
                    <i className="fab fa-twitter"></i>
                  </a>
                </li>
              </ul>
            </div>
          </div>
          {/* Showcase NFT */}
          <div className={`block-main ${isViewingCollection && !isTransactionBusy ? 'active' : ''}`}>
            <div className="block-content">
              <NFTShowcase
                startingIndex={startingIndex}
                hasMinted={hasMinted}
                isLoading={showcaseNFTSIsLoading}
                nfts={showcaseNFTS.length > 0 ? showcaseNFTS : boughtNFTS}
                isViewingCollection={isViewingCollection}
              />
            </div>
            <div className="block-footer">
              <div className="footer-copyright">
                &copy; Copyright <span id="copyright-year">2021</span> Cacti Club
              </div>
              <ul className="footer-social">
                <li>
                  <a href="https://discord.com/invite/ZvZSbhEmdR" target="_blank" title="Discord" rel="noreferrer">
                    <i className="fab fa-discord"></i>
                  </a>
                </li>
                <li>
                  <a href="https://twitter.com/TheCactiClub" target="_blank" title="Twitter" rel="noreferrer">
                    <i className="fab fa-twitter"></i>
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className={`block-main ${loaderDialogState ? 'active' : ''}`}>
            <div className="block-content">
              <LoaderDialog open={loaderDialogState} title={loadingText[0]} subtitle={loadingText[1]} />
            </div>
            <div className="block-footer">
              <div className="footer-copyright">
                &copy; Copyright <span id="copyright-year">2021</span> Cacti Club
              </div>
              <ul className="footer-social">
                <li>
                  <a href="https://discord.com/invite/ZvZSbhEmdR" target="_blank" title="Discord" rel="noreferrer">
                    <i className="fab fa-discord"></i>
                  </a>
                </li>
                <li>
                  <a href="https://twitter.com/TheCactiClub" target="_blank" title="Twitter" rel="noreferrer">
                    <i className="fab fa-twitter"></i>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <TextDialog
        open={textDialogState}
        onClose={() => setTextDialogState(false)}
      />
    </>
  )
}

export default Home
